import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowBack, IoCall, IoVideocam, IoCopy, IoCheckmark } from 'react-icons/io5';
import { useAnonymousCall } from '../hooks/useAnonymousCall';
import AnonymousCallModal from './call/AnonymousCallModal';

type Screen = 'choice' | 'create' | 'join';

const CallWithoutLoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('choice');
  const [callKeyInput, setCallKeyInput] = useState('');
  const [userName, setUserName] = useState('');
  const [copied, setCopied] = useState(false);
  
  const {
    callState,
    callKey,
    role,
    error,
    callTimer,
    isMuted,
    isVideoOff,
    cameraFacingMode,
    remoteName,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    createSession,
    joinSession,
    endCall,
    toggleMute,
    toggleVideo,
    switchCamera,
    setError
  } = useAnonymousCall();

  // Handle end call
  const handleEndCall = async () => {
    await endCall();
    // Return to choice screen after call ends
    setScreen('choice');
    setCallKeyInput('');
  };

  // Copy call key to clipboard
  const copySessionId = () => {
    if (callKey) {
      navigator.clipboard.writeText(callKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle create session
  const handleCreateSession = async () => {
    const name = userName.trim() || 'Host';
    const key = callKeyInput.trim();
    if (!key) {
      setError('Please enter a call key');
      return;
    }

    const id = await createSession(key, name);
    if (id) {
      setScreen('create');
    }
  };

  // Handle join session
  const handleJoinSession = async () => {
    const key = callKeyInput.trim();
    if (!key) {
      setError('Please enter a call key');
      return;
    }
    
    const name = userName.trim() || 'Guest';
    const success = await joinSession(key, name);
    if (success) {
      setScreen('join');
    }
  };

  // Get status text based on call state
  const getStatusText = () => {
    switch (callState) {
      case 'waiting':
        return 'Waiting for guest to join...';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Connected';
      case 'ended':
        return 'Call ended';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm px-4 py-4 flex items-center gap-4 border-b border-slate-700">
        <button
          onClick={() => {
            if (screen === 'choice') {
              navigate('/');
            } else {
              setScreen('choice');
              setCallKeyInput('');
              setError(null);
            }
          }}
          className="text-slate-300 hover:text-white transition-colors"
        >
          <IoArrowBack size={24} />
        </button>
        <h1 className="text-xl font-semibold text-white">Call Without Login</h1>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        {/* Choice Screen */}
        {screen === 'choice' && (
          <div className="max-w-md w-full space-y-6">
            <div className="text-center mb-8">
              <IoVideocam size={64} className="text-blue-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Quick Call</h2>
              <p className="text-slate-300">
                Make instant video calls without signing in
              </p>
            </div>

            {/* Optional Name Input */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Your Name (Optional)
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={30}
              />
            </div>

            {/* Call Key Input */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Call Key
              </label>
              <input
                type="text"
                value={callKeyInput}
                onChange={(e) => setCallKeyInput(e.target.value.replace(/[^A-Za-z0-9@_-]/g, ''))}
                placeholder="e.g., pajani@12"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-center text-xl font-mono tracking-wide placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={50}
              />
            </div>

            {/* Create Session Button */}
            <button
              onClick={handleCreateSession}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg"
            >
              <IoCall size={24} />
              Start Call
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-slate-900 bg-opacity-50 text-slate-400">OR</span>
              </div>
            </div>

            {/* Join Button */}
            <button
              onClick={handleJoinSession}
              disabled={!callKeyInput.trim()}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg"
            >
              <IoVideocam size={24} />
              Join Call
            </button>

            {/* Error Message */}
            {error && (
              <div className="bg-red-900 bg-opacity-50 border border-red-600 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Create Session Screen (Host) */}
        {screen === 'create' && (
          <div className="max-w-md w-full space-y-6">
            <div className="bg-slate-800 bg-opacity-70 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-slate-700">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <IoCall size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Your Call Key</h2>
                <p className="text-slate-300 text-sm">
                  Share this key with the person you want to call
                </p>
              </div>

              {/* Session ID Display */}
              <div className="bg-slate-900 rounded-lg p-6 mb-6 border-2 border-blue-500">
                <div className="text-center">
                  <p className="text-sm text-slate-400 mb-2">Call Key</p>
                  <p className="text-5xl font-bold text-white font-mono tracking-wider">
                    {callKey || '--------'}
                  </p>
                </div>
              </div>

              {/* Copy Button */}
              <button
                onClick={copySessionId}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 mb-6"
              >
                {copied ? (
                  <>
                    <IoCheckmark size={20} />
                    Copied!
                  </>
                ) : (
                  <>
                    <IoCopy size={20} />
                    Copy Call Key
                  </>
                )}
              </button>

              {/* Status */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-blue-900 bg-opacity-50 px-4 py-2 rounded-full">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <span className="text-blue-200 text-sm font-medium">
                    {getStatusText()}
                  </span>
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-6 pt-6 border-t border-slate-700">
                <p className="text-slate-400 text-sm text-center">
                  Share this key via WhatsApp, SMS, or phone call. The other person should click "Join Call" and enter this key.
                </p>
              </div>
            </div>

            {/* Cancel Button */}
            <button
              onClick={handleEndCall}
              className="w-full bg-red-900 bg-opacity-50 hover:bg-red-900 hover:bg-opacity-70 text-red-200 font-semibold py-3 px-6 rounded-lg transition-colors border border-red-700"
            >
              Cancel Call
            </button>
          </div>
        )}

        {/* Join Session Screen (Guest) */}
        {screen === 'join' && callState === 'connecting' && (
          <div className="max-w-md w-full">
            <div className="bg-slate-800 bg-opacity-70 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-slate-700 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <IoVideocam size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{getStatusText()}</h2>
              <p className="text-slate-300">Please wait...</p>
            </div>
          </div>
        )}
      </div>

      {/* Call Modal - Only show when call is actually connected */}
      <AnonymousCallModal
        isOpen={callState === 'connected'}
        callStatus={getStatusText()}
        callTimer={callTimer}
        remoteName={remoteName}
        localVideoRef={localVideoRef}
        remoteVideoRef={remoteVideoRef}
        remoteAudioRef={remoteAudioRef}
        onEndCall={handleEndCall}
        onToggleMute={toggleMute}
        onToggleVideo={toggleVideo}
        onSwitchCamera={switchCamera}
        isMuted={isMuted}
        isVideoOff={isVideoOff}
        cameraFacingMode={cameraFacingMode}
      />
    </div>
  );
};

export default CallWithoutLoginScreen;
